import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { RouterStatsEntry } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useRouterStats } from '../api/useRouterStats.ts';
import { DashboardAlerts } from '../components/DashboardAlerts.tsx';
import { formatUsdCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * Mission Control dashboard: per-upstream router stats over the last
 * N days, sourced from `ops-api /v1/ops/admin/router-stats` (proxy to
 * gateway `/internal/router-stats`).
 *
 * Data flow:
 *   - `days` state drives both the dropdown and the React Query key, so
 *     switching window hits its own cache slot.
 *   - Raw entries from the backend are bucketed by `(date, upstreamId)`;
 *     we fold them into per-upstream aggregates client-side:
 *       - total requests / failures across the window
 *       - failure rate = failures / requests
 *       - latency = weighted mean of per-day latency*requests, since the
 *         backend column is AVG today — aggregating AVGs weighted by
 *         requests gives the correct window-wide AVG (p95 approx is
 *         still an approximation, labelled "p95 (avg)").
 *       - totalCostUsdCents summed.
 *
 * Visual contract follows DESIGN.md §3 (display-md metric) and §6 (cards
 * on `surface-container`). No chart library is pulled in — a sortable
 * table is enough for Phase-1 operator triage.
 */

const WINDOW_OPTIONS: readonly {
  readonly value: 7 | 30 | 90;
  readonly labelKey: I18nKey;
}[] = [
  { value: 7, labelKey: 'dashboard.window.7d' },
  { value: 30, labelKey: 'dashboard.window.30d' },
  { value: 90, labelKey: 'dashboard.window.90d' },
];

type SortKey = 'upstream' | 'requests' | 'failureRate' | 'latency' | 'cost';
type SortDir = 'asc' | 'desc';

interface UpstreamAggregate {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly requests: number;
  readonly failures: number;
  readonly failureRate: number;
  /** null when every bucket lacked a latency reading. */
  readonly latencyMs: number | null;
  readonly costUsdCents: number;
}

function aggregate(entries: readonly RouterStatsEntry[]): readonly UpstreamAggregate[] {
  const acc = new Map<
    string,
    {
      upstreamId: string;
      upstreamName: string;
      requests: number;
      failures: number;
      costUsdCents: number;
      // Weighted-mean accumulator for latency: sum(latency * requests)
      // over buckets that reported latency, and the request count of
      // those buckets (requests-with-latency).
      latencyWeightedSum: number;
      latencyWeight: number;
    }
  >();
  for (const entry of entries) {
    const existing = acc.get(entry.upstreamId);
    if (existing === undefined) {
      acc.set(entry.upstreamId, {
        upstreamId: entry.upstreamId,
        upstreamName: entry.upstreamName,
        requests: entry.requests,
        failures: entry.failures,
        costUsdCents: entry.costUsdCents,
        latencyWeightedSum:
          entry.p95LatencyMs === null ? 0 : entry.p95LatencyMs * entry.requests,
        latencyWeight: entry.p95LatencyMs === null ? 0 : entry.requests,
      });
      continue;
    }
    existing.requests += entry.requests;
    existing.failures += entry.failures;
    existing.costUsdCents += entry.costUsdCents;
    // Keep the latest non-empty name we saw (backend may return empty
    // for hard-deleted upstreams; prefer a concrete label when any row
    // has one).
    if (entry.upstreamName.length > 0) {
      existing.upstreamName = entry.upstreamName;
    }
    if (entry.p95LatencyMs !== null) {
      existing.latencyWeightedSum += entry.p95LatencyMs * entry.requests;
      existing.latencyWeight += entry.requests;
    }
  }
  const out: UpstreamAggregate[] = [];
  for (const v of acc.values()) {
    out.push({
      upstreamId: v.upstreamId,
      upstreamName: v.upstreamName,
      requests: v.requests,
      failures: v.failures,
      failureRate: v.requests === 0 ? 0 : v.failures / v.requests,
      latencyMs: v.latencyWeight === 0 ? null : v.latencyWeightedSum / v.latencyWeight,
      costUsdCents: v.costUsdCents,
    });
  }
  return out;
}

function compare(a: UpstreamAggregate, b: UpstreamAggregate, key: SortKey): number {
  switch (key) {
    case 'upstream': {
      const an = a.upstreamName.length > 0 ? a.upstreamName : a.upstreamId;
      const bn = b.upstreamName.length > 0 ? b.upstreamName : b.upstreamId;
      return an.localeCompare(bn);
    }
    case 'requests':
      return a.requests - b.requests;
    case 'failureRate':
      return a.failureRate - b.failureRate;
    case 'latency': {
      // Nulls sort last in ascending order.
      if (a.latencyMs === null && b.latencyMs === null) return 0;
      if (a.latencyMs === null) return 1;
      if (b.latencyMs === null) return -1;
      return a.latencyMs - b.latencyMs;
    }
    case 'cost':
      return a.costUsdCents - b.costUsdCents;
  }
}

function formatRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  return `${(rate * 100).toFixed(2)}%`;
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

function formatLatency(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(value).toLocaleString('en-US')} ms`;
}

interface StatTotals {
  readonly upstreams: number;
  readonly requests: number;
  readonly failureRate: number;
  readonly costUsdCents: number;
}

function sumTotals(rows: readonly UpstreamAggregate[]): StatTotals {
  let requests = 0;
  let failures = 0;
  let costUsdCents = 0;
  for (const r of rows) {
    requests += r.requests;
    failures += r.failures;
    costUsdCents += r.costUsdCents;
  }
  return {
    upstreams: rows.length,
    requests,
    failureRate: requests === 0 ? 0 : failures / requests,
    costUsdCents,
  };
}

function parseDays(raw: string): 7 | 30 | 90 {
  switch (raw) {
    case '30':
      return 30;
    case '90':
      return 90;
    default:
      return 7;
  }
}

export function Dashboard(): JSX.Element {
  const [days, setDays] = useState<7 | 30 | 90>(7);
  const [sortKey, setSortKey] = useState<SortKey>('requests');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const query = useRouterStats(days);
  const t = useT();

  const aggregates = useMemo(() => {
    if (query.data === undefined) return [];
    return aggregate(query.data.stats);
  }, [query.data]);

  const sorted = useMemo(() => {
    const copy = aggregates.slice();
    copy.sort((a, b) => {
      const base = compare(a, b, sortKey);
      return sortDir === 'asc' ? base : -base;
    });
    return copy;
  }, [aggregates, sortKey, sortDir]);

  const totals = useMemo(() => sumTotals(aggregates), [aggregates]);

  function onWindowChange(event: ChangeEvent<HTMLSelectElement>): void {
    setDays(parseDays(event.currentTarget.value));
  }

  function toggleSort(key: SortKey): void {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Default direction: text ascending, numeric descending.
    setSortDir(key === 'upstream' ? 'asc' : 'desc');
  }

  return (
    <section data-testid="dashboard-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-2">
          <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
            {t('dashboard.title')}
          </h2>
          <p className="text-sm text-outline">{t('dashboard.subtitle')}</p>
        </div>
        <label className="flex items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('dashboard.window.label')}
          </span>
          <select
            data-testid="dashboard-window"
            value={days.toString()}
            onChange={onWindowChange}
            className="rounded-lg bg-surface-container-lowest px-3 py-2 text-sm text-on-surface border-0 border-b border-outline-variant focus:border-primary focus:outline-none focus:ring-0 transition-colors"
          >
            {WINDOW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value.toString()}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </label>
      </header>

      <DashboardAlerts />

      <div
        data-testid="dashboard-stat-grid"
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
      >
        <SummaryCard
          testId="dashboard-stat-upstreams"
          label={t('dashboard.stat.upstreams.label')}
          value={query.isSuccess ? formatNumber(totals.upstreams) : '—'}
          delta={t('dashboard.stat.upstreams.delta', { days })}
        />
        <SummaryCard
          testId="dashboard-stat-requests"
          label={t('dashboard.stat.requests.label')}
          value={query.isSuccess ? formatNumber(totals.requests) : '—'}
          delta={t('dashboard.stat.requests.delta', { days })}
        />
        <SummaryCard
          testId="dashboard-stat-failure-rate"
          label={t('dashboard.stat.failure_rate.label')}
          value={query.isSuccess ? formatRate(totals.failureRate) : '—'}
          delta={t('dashboard.stat.failure_rate.delta')}
        />
        <SummaryCard
          testId="dashboard-stat-cost"
          label={t('dashboard.stat.cost.label')}
          value={query.isSuccess ? formatUsdCents(totals.costUsdCents) : '—'}
          delta={t('dashboard.stat.cost.delta')}
        />
      </div>

      {query.isPending ? (
        <div
          data-testid="dashboard-loading"
          className="flex flex-col gap-2 rounded-xl bg-surface-container p-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
          <span className="sr-only">{t('dashboard.loading_aria')}</span>
        </div>
      ) : null}

      {query.isError ? (
        <div
          role="alert"
          data-testid="dashboard-error"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('dashboard.error.title')}</span>
            <span className="text-xs">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void query.refetch()}
            data-testid="dashboard-retry"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {query.isSuccess && sorted.length === 0 ? (
        <div
          data-testid="dashboard-empty"
          className="flex flex-col items-center gap-4 rounded-xl bg-surface-container px-8 py-16 text-center"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-on-surface">
            {t('dashboard.empty.title')}
          </h3>
          <p className="max-w-md text-sm text-outline">
            {t('dashboard.empty.body_prefix', { days })}
            <code className="font-mono">usage_logs</code>
            {t('dashboard.empty.body_suffix', { days })}
          </p>
        </div>
      ) : null}

      {query.isSuccess && sorted.length > 0 ? (
        <div className="overflow-hidden rounded-xl bg-surface-container">
          <table
            data-testid="dashboard-table"
            className="w-full border-separate border-spacing-0"
          >
            <thead>
              <tr className="bg-surface-container-high">
                <SortableHeader
                  testId="dashboard-sort-upstream"
                  label={t('dashboard.column.upstream')}
                  active={sortKey === 'upstream'}
                  dir={sortDir}
                  onToggle={() => toggleSort('upstream')}
                  align="left"
                />
                <SortableHeader
                  testId="dashboard-sort-requests"
                  label={t('dashboard.column.requests')}
                  active={sortKey === 'requests'}
                  dir={sortDir}
                  onToggle={() => toggleSort('requests')}
                  align="right"
                />
                <SortableHeader
                  testId="dashboard-sort-failure-rate"
                  label={t('dashboard.column.failure_rate')}
                  active={sortKey === 'failureRate'}
                  dir={sortDir}
                  onToggle={() => toggleSort('failureRate')}
                  align="right"
                />
                <SortableHeader
                  testId="dashboard-sort-latency"
                  label={t('dashboard.column.latency')}
                  active={sortKey === 'latency'}
                  dir={sortDir}
                  onToggle={() => toggleSort('latency')}
                  align="right"
                />
                <SortableHeader
                  testId="dashboard-sort-cost"
                  label={t('dashboard.column.cost')}
                  active={sortKey === 'cost'}
                  dir={sortDir}
                  onToggle={() => toggleSort('cost')}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <UpstreamRow
                  key={row.upstreamId}
                  row={row}
                  zebra={i % 2 === 0 ? 'odd' : 'even'}
                  deletedLabel={t('dashboard.row.deleted_upstream')}
                />
              ))}
            </tbody>
          </table>
          <p className="px-4 py-3 text-[11px] text-outline">{t('dashboard.latency_note')}</p>
        </div>
      ) : null}
    </section>
  );
}

interface SummaryCardProps {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly delta: string;
}

function SummaryCard({ testId, label, value, delta }: SummaryCardProps): JSX.Element {
  return (
    <article
      data-testid={testId}
      className="flex flex-col gap-3 rounded-xl bg-surface-container p-6"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label}
      </span>
      <span className="text-4xl font-semibold tracking-tight text-primary">{value}</span>
      <span className="text-xs text-outline">{delta}</span>
    </article>
  );
}

interface SortableHeaderProps {
  readonly testId: string;
  readonly label: string;
  readonly active: boolean;
  readonly dir: SortDir;
  readonly onToggle: () => void;
  readonly align: 'left' | 'right';
}

function SortableHeader({
  testId,
  label,
  active,
  dir,
  onToggle,
  align,
}: SortableHeaderProps): JSX.Element {
  const alignClass = align === 'left' ? 'text-left' : 'text-right';
  const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={`px-4 py-3 ${alignClass} text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={testId}
        className="inline-flex items-center gap-1 hover:text-on-surface focus-visible:outline-none focus-visible:text-on-surface"
      >
        <span>{label}</span>
        <span aria-hidden="true" className="text-[9px] text-outline-variant">
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

interface UpstreamRowProps {
  readonly row: UpstreamAggregate;
  readonly zebra: 'odd' | 'even';
  readonly deletedLabel: string;
}

function UpstreamRow({ row, zebra, deletedLabel }: UpstreamRowProps): JSX.Element {
  const zebraClass = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  const nameLabel = row.upstreamName.length > 0 ? row.upstreamName : deletedLabel;
  return (
    <tr
      data-testid={`dashboard-row-${row.upstreamId}`}
      className={`${zebraClass} transition-colors hover:bg-surface-bright`}
    >
      <td className="px-4 py-3 text-[13px] text-on-surface">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">{nameLabel}</span>
          <span className="font-mono text-[11px] text-outline">{row.upstreamId}</span>
        </div>
      </td>
      <td
        data-testid={`dashboard-row-${row.upstreamId}-requests`}
        className="px-4 py-3 text-right font-mono text-[12px] text-on-surface"
      >
        {formatNumber(row.requests)}
      </td>
      <td
        data-testid={`dashboard-row-${row.upstreamId}-failure-rate`}
        className="px-4 py-3 text-right font-mono text-[12px] text-on-surface"
      >
        {formatRate(row.failureRate)}
      </td>
      <td
        data-testid={`dashboard-row-${row.upstreamId}-latency`}
        className="px-4 py-3 text-right font-mono text-[12px] text-on-surface"
      >
        {formatLatency(row.latencyMs)}
      </td>
      <td
        data-testid={`dashboard-row-${row.upstreamId}-cost`}
        className="px-4 py-3 text-right font-mono text-[12px] text-on-surface"
      >
        {formatUsdCents(row.costUsdCents)}
      </td>
    </tr>
  );
}
